import React, { useState, useEffect, useRef, useCallback } from 'react';
import { gamesData as mockGamesData } from '../data/mock';
import { ExternalLink } from 'lucide-react';

const DOT_SIZE = 64;
const MOBILE_DOT_SIZE = DOT_SIZE * 0.75 * 0.85;
const DOT_GAP_PX = 38; // ~1cm on standard 96dpi screens
const REACTIVITY_MULTIPLIER = 1.5;
const FLEE_RADIUS_PX = 230;
const FLEE_ACCEL = 1.25;
const SPEED_BOOST = 0.08;
const TOUCH_FLEE_RADIUS_MULTIPLIER = 1.15;
const TOUCH_FLEE_ACCEL_MULTIPLIER = 1.25;
const TOUCH_SPEED_BOOST_MULTIPLIER = 1.3;
const FRICTION = 0.94;
const MAX_SPEED = 13;
const SEPARATION_DISTANCE = DOT_SIZE + 20;
const SEPARATION_FORCE = 0.28;
const DOT_COLORS = ['#F5B041', '#5B9BD5', '#1A532F'];

const GamesSection = ({ data }) => {
  const gamesData = data || mockGamesData;
  const [circles, setCircles] = useState([]);
  const [isCoarsePointer, setIsCoarsePointer] = useState(false);
  const sectionRef = useRef(null);
  const dotAnchorRef = useRef(null);
  const physicsRef = useRef([]);
  const mouseRef = useRef({ x: 0, y: 0, speed: 0, active: false, pointerType: 'mouse' });
  const lastPointerRef = useRef(null);
  const dotSize = isCoarsePointer ? MOBILE_DOT_SIZE : DOT_SIZE;
  const dotSpacingPx = dotSize + DOT_GAP_PX;
  const separationDistance = dotSize + 20;

  useEffect(() => {
    const coarseQuery = window.matchMedia('(pointer: coarse)');
    setIsCoarsePointer(coarseQuery.matches);

    const handleChange = (event) => setIsCoarsePointer(event.matches);
    coarseQuery.addEventListener('change', handleChange);

    return () => coarseQuery.removeEventListener('change', handleChange);
  }, []);

  const getAnchorPoint = useCallback(() => {
    if (!sectionRef.current || !dotAnchorRef.current) {
      return null;
    }

    const sectionRect = sectionRef.current.getBoundingClientRect();
    const anchorRect = dotAnchorRef.current.getBoundingClientRect();
    return {
      x: anchorRect.left - sectionRect.left + anchorRect.width / 2,
      y: anchorRect.top - sectionRect.top + anchorRect.height / 2
    };
  }, []);

  const buildDotLayout = useCallback((width, height, previousDots = []) => {
    const margin = dotSize / 2 + 6;
    const anchor = getAnchorPoint();
    const centerX = anchor?.x || width / 2;
    const centerY = anchor?.y || height / 2;
    const minimumX = margin;
    const maximumX = width - margin;
    const minimumY = margin;
    const maximumY = height - margin;
    const maxSpacingByWidth = Math.max(22, (width - margin * 2) / 2);
    const spacing = Math.min(dotSpacingPx, maxSpacingByWidth);

    return DOT_COLORS.map((color, index) => {
      const offset = (index - 1) * spacing;
      const baseX = Math.max(minimumX, Math.min(maximumX, centerX + offset));
      const prior = previousDots[index];
      return {
        id: index + 1,
        color,
        size: dotSize,
        x: prior ? Math.max(minimumX, Math.min(maximumX, prior.x)) : baseX,
        y: prior ? Math.max(minimumY, Math.min(maximumY, prior.y)) : centerY,
        vx: prior?.vx || 0,
        vy: prior?.vy || 0
      };
    });
  }, [getAnchorPoint, dotSize, dotSpacingPx]);

  useEffect(() => {
    if (isCoarsePointer) {
      physicsRef.current = [];
      setCircles([]);
      return undefined;
    }

    const resetLayout = () => {
      if (!sectionRef.current) {
        return;
      }

      const rect = sectionRef.current.getBoundingClientRect();
      const layout = buildDotLayout(rect.width, rect.height, physicsRef.current);
      physicsRef.current = layout;
      setCircles(layout);
    };

    resetLayout();
    window.addEventListener('resize', resetLayout);

    return () => {
      window.removeEventListener('resize', resetLayout);
    };
  }, [buildDotLayout, isCoarsePointer]);

  useEffect(() => {
    if (isCoarsePointer) {
      return undefined;
    }

    const updatePointerState = (e, active) => {
      if (!sectionRef.current) {
        return;
      }

      const rect = sectionRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const now = performance.now();
      const lastPointer = lastPointerRef.current;

      let speed = 0;
      if (lastPointer) {
        const dx = x - lastPointer.x;
        const dy = y - lastPointer.y;
        const dt = Math.max(16, now - lastPointer.t);
        speed = Math.sqrt(dx * dx + dy * dy) / dt;
      }

      lastPointerRef.current = { x, y, t: now };
      mouseRef.current = {
        x,
        y,
        speed,
        active,
        pointerType: e.pointerType || 'mouse'
      };
    };

    const handlePointerDown = (e) => {
      if (sectionRef.current && e.pointerType === 'touch') {
        sectionRef.current.setPointerCapture(e.pointerId);
      }
      updatePointerState(e, true);
    };

    const handlePointerMove = (e) => {
      updatePointerState(e, true);
    };

    const handlePointerEnd = (e) => {
      if (sectionRef.current && e.pointerType === 'touch' && sectionRef.current.hasPointerCapture(e.pointerId)) {
        sectionRef.current.releasePointerCapture(e.pointerId);
      }
      mouseRef.current.active = false;
      lastPointerRef.current = null;
    };

    const section = sectionRef.current;
    if (section) {
      section.addEventListener('pointerdown', handlePointerDown);
      section.addEventListener('pointermove', handlePointerMove);
      section.addEventListener('pointerup', handlePointerEnd);
      section.addEventListener('pointercancel', handlePointerEnd);
      section.addEventListener('pointerleave', handlePointerEnd);
    }

    return () => {
      if (section) {
        section.removeEventListener('pointerdown', handlePointerDown);
        section.removeEventListener('pointermove', handlePointerMove);
        section.removeEventListener('pointerup', handlePointerEnd);
        section.removeEventListener('pointercancel', handlePointerEnd);
        section.removeEventListener('pointerleave', handlePointerEnd);
      }
    };
  }, [isCoarsePointer]);

  useEffect(() => {
    if (isCoarsePointer) {
      return undefined;
    }

    let frameId;

    const animate = () => {
      const activeDots = physicsRef.current;
      const mouse = mouseRef.current;

      if (sectionRef.current && activeDots.length > 0) {
        const rect = sectionRef.current.getBoundingClientRect();
        const minX = dotSize / 2 + 4;
        const maxX = rect.width - dotSize / 2 - 4;
        const minY = dotSize / 2 + 4;
        const maxY = rect.height - dotSize / 2 - 4;

        const updated = activeDots.map((dot, index) => {
          let ax = 0;
          let ay = 0;

          if (mouse.active) {
            const dx = dot.x - mouse.x;
            const dy = dot.y - mouse.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            const touchMode = mouse.pointerType === 'touch';
            const fleeRadius = touchMode ? FLEE_RADIUS_PX * TOUCH_FLEE_RADIUS_MULTIPLIER : FLEE_RADIUS_PX;
            const fleeAccel = touchMode ? FLEE_ACCEL * TOUCH_FLEE_ACCEL_MULTIPLIER : FLEE_ACCEL;
            const speedBoost = touchMode ? SPEED_BOOST * TOUCH_SPEED_BOOST_MULTIPLIER : SPEED_BOOST;

            if (distance < fleeRadius) {
              const safeDistance = Math.max(distance, 0.001);
              const forceRatio = (fleeRadius - safeDistance) / fleeRadius;
              const panicForce =
                Math.pow(forceRatio, 1.3) *
                fleeAccel *
                REACTIVITY_MULTIPLIER *
                (1 + mouse.speed * speedBoost);

              ax += (dx / safeDistance) * panicForce;
              ay += (dy / safeDistance) * panicForce;
            }
          }

          activeDots.forEach((otherDot, otherIndex) => {
            if (otherIndex === index) {
              return;
            }

            const dx = dot.x - otherDot.x;
            const dy = dot.y - otherDot.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance > 0 && distance < separationDistance) {
              const ratio = (separationDistance - distance) / separationDistance;
              ax += (dx / distance) * ratio * SEPARATION_FORCE;
              ay += (dy / distance) * ratio * SEPARATION_FORCE;
            }
          });

          let vx = (dot.vx + ax) * FRICTION;
          let vy = (dot.vy + ay) * FRICTION;
          const speed = Math.sqrt(vx * vx + vy * vy);
          if (speed > MAX_SPEED) {
            const clamp = MAX_SPEED / speed;
            vx *= clamp;
            vy *= clamp;
          }

          let x = dot.x + vx;
          let y = dot.y + vy;

          if (x < minX) {
            x = maxX;
          } else if (x > maxX) {
            x = minX;
          }

          if (y < minY) {
            y = maxY;
          } else if (y > maxY) {
            y = minY;
          }

          return {
            ...dot,
            x,
            y,
            vx,
            vy
          };
        });

        physicsRef.current = updated;
        setCircles(updated);
      }

      frameId = window.requestAnimationFrame(animate);
    };

    frameId = window.requestAnimationFrame(animate);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [dotSize, separationDistance, isCoarsePointer]);

  return (
    <section
      id="games"
      ref={sectionRef}
      className={`min-h-[90vh] bg-[#E64A2B] flex flex-col items-center justify-center py-20 px-6 relative overflow-hidden select-none ${
        isCoarsePointer ? 'touch-pan-y' : 'touch-none'
      }`}
    >
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute top-20 left-20 w-32 h-32 border-8 border-white rounded-lg transform rotate-45"></div>
        <div className="absolute bottom-32 right-32 w-40 h-40 border-8 border-white rounded-full"></div>
        <div className="absolute top-1/2 left-1/3 w-24 h-24 border-8 border-white rounded-lg"></div>
      </div>

      <div className="max-w-5xl mx-auto text-center relative z-10">
        {/* Big "PLAY" text with Hydrogen Whiskey font */}
        <h2
          className="text-9xl md:text-[14rem] lg:text-[18rem] font-black text-white mb-8 leading-none"
          style={{
            fontFamily: "'Hydrogen Whiskey', 'Press Start 2P', monospace",
            letterSpacing: '0.15em'
          }}
        >
          PLAY
        </h2>

        {/* Subtitle */}
        <p className="text-3xl md:text-5xl text-white/90 font-light mb-8 max-w-3xl mx-auto">
          {gamesData.subtitle}
        </p>

        {/* Game Link Button */}
        <a
          href={gamesData.gameLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center space-x-4 bg-white text-[#E64A2B] px-10 py-6 rounded-full text-2xl font-bold hover:bg-[#1A532F] hover:text-white transition-all duration-300 transform hover:scale-105 hover:shadow-2xl group mb-12"
          style={{ fontFamily: "'VT323', monospace" }}
        >
          <span>{gamesData.gameName}</span>
          <ExternalLink className="group-hover:translate-x-1 transition-transform" size={28} />
        </a>

        <div ref={dotAnchorRef} className="h-16 w-full" />
      </div>

      {!isCoarsePointer &&
        circles.map((circle) => (
          <div
            key={circle.id}
            className="absolute transform -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none will-change-transform z-20"
            style={{
              left: `${circle.x}px`,
              top: `${circle.y}px`,
              width: `${circle.size}px`,
              height: `${circle.size}px`,
              backgroundColor: circle.color,
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
            }}
          />
        ))}
    </section>
  );
};

export default GamesSection;
